import React, { useMemo } from "react";
import { Button, Form } from "react-bootstrap";
import { FiPlus, FiTrash2 } from "react-icons/fi";
import type { KpiDataType } from "../types/kpiDataTypes";
import type { KpiGridColumn, KpiGridColumnOption } from "../types/kpiEntryMode";
import {
  isGridDataTypeName,
  isMultiselectDataTypeName,
  isNumericCompatibleDataTypeName,
  isSelectDataTypeName,
} from "../types/kpiEntryMode";

type Props = {
  columns: KpiGridColumn[];
  gridMinRows: number | "";
  gridMaxRows: number | "";
  gridShowColumnTotals: boolean;
  gridShowRowTotals: boolean;
  dataTypes: KpiDataType[];
  onChangeColumns: (next: KpiGridColumn[]) => void;
  onChangeMinRows: (value: number | "") => void;
  onChangeMaxRows: (value: number | "") => void;
  onChangeShowColumnTotals: (value: boolean) => void;
  onChangeShowRowTotals: (value: boolean) => void;
};

function renumber(columns: KpiGridColumn[]): KpiGridColumn[] {
  return columns.map((col, idx) => ({ ...col, list_order: idx }));
}

function renumberOptions(options: KpiGridColumnOption[]): KpiGridColumnOption[] {
  return options.map((opt, idx) => ({ ...opt, list_order: idx }));
}

function splitColumnOptions(options: KpiGridColumnOption[]): {
  normal: KpiGridColumnOption[];
  other: KpiGridColumnOption | null;
} {
  const other = options.find((o) => !!o.allows_input) ?? null;
  const normal = options.filter((o) => !o.allows_input);
  return { normal, other };
}

const KpiGridColumnsEditor: React.FC<Props> = ({
  columns,
  gridMinRows,
  gridMaxRows,
  gridShowColumnTotals,
  gridShowRowTotals,
  dataTypes,
  onChangeColumns,
  onChangeMinRows,
  onChangeMaxRows,
  onChangeShowColumnTotals,
  onChangeShowRowTotals,
}) => {
  const dataTypeById = useMemo(() => {
    const map = new Map<number, KpiDataType>();
    for (const dt of dataTypes) map.set(dt.data_type_id, dt);
    return map;
  }, [dataTypes]);

  const defaultDataTypeId = dataTypes[0]?.data_type_id ?? 0;

  const addColumn = () => {
    const dt = dataTypeById.get(defaultDataTypeId);
    const name = dt?.data_type_name ?? "";
    onChangeColumns(
      renumber([
        ...columns,
        {
          label: "",
          data_type_id: defaultDataTypeId,
          list_order: columns.length,
          include_in_rollup: isNumericCompatibleDataTypeName(name),
          is_active: true,
          options: [],
        },
      ])
    );
  };

  const updateColumn = (index: number, patch: Partial<KpiGridColumn>) => {
    const next = columns.map((col, i) => {
      if (i !== index) return col;
      const merged = { ...col, ...patch };

      if (patch.data_type_id !== undefined) {
        const dt = dataTypeById.get(Number(patch.data_type_id));
        const name = dt?.data_type_name ?? "";
        merged.include_in_rollup = isNumericCompatibleDataTypeName(name);
        if (!isSelectDataTypeName(name) && !isMultiselectDataTypeName(name)) {
          merged.options = [];
        }
      }

      if (patch.options !== undefined) {
        const { normal, other } = splitColumnOptions(patch.options);
        merged.options = renumberOptions(
          other ? [...normal, other] : normal
        );
      }

      return merged;
    });
    onChangeColumns(renumber(next));
  };

  const removeColumn = (index: number) => {
    onChangeColumns(renumber(columns.filter((_, i) => i !== index)));
  };

  const setColumnOptions = (
    columnIndex: number,
    nextNormal: KpiGridColumnOption[],
    nextOther: KpiGridColumnOption | null
  ) => {
    updateColumn(columnIndex, {
      options: renumberOptions(
        nextOther ? [...nextNormal, nextOther] : nextNormal
      ),
    });
  };

  return (
    <div data-testid="kpi-grid-columns-editor">
      <div className="d-flex flex-wrap gap-3 mb-2">
        <Form.Group controlId="gridMinRows" style={{ maxWidth: 160 }}>
          <Form.Label>Min free rows</Form.Label>
          <Form.Control
            type="number"
            min={0}
            value={gridMinRows === "" ? "" : gridMinRows}
            onChange={(e) =>
              onChangeMinRows(e.target.value === "" ? "" : Number(e.target.value))
            }
          />
        </Form.Group>
        <Form.Group controlId="gridMaxRows" style={{ maxWidth: 160 }}>
          <Form.Label>Max free rows</Form.Label>
          <Form.Control
            type="number"
            min={1}
            value={gridMaxRows === "" ? "" : gridMaxRows}
            onChange={(e) =>
              onChangeMaxRows(e.target.value === "" ? "" : Number(e.target.value))
            }
          />
        </Form.Group>
        <Form.Group className="d-flex align-items-end">
          <Form.Check
            type="switch"
            id="gridShowColumnTotals"
            label="Show column totals"
            checked={gridShowColumnTotals}
            onChange={(e) => onChangeShowColumnTotals(e.target.checked)}
          />
        </Form.Group>
        <Form.Group className="d-flex align-items-end">
          <Form.Check
            type="switch"
            id="gridShowRowTotals"
            label="Show row totals"
            checked={gridShowRowTotals}
            onChange={(e) => onChangeShowRowTotals(e.target.checked)}
          />
        </Form.Group>
      </div>
      <Form.Text className="text-muted d-block mb-3">
        Row headings (including fixed rows) appear in the first column.
      </Form.Text>

      <div className="d-flex justify-content-between align-items-center mb-2">
        <div className="fw-semibold">Columns</div>
        <Button
          variant="outline-primary"
          size="sm"
          className="btn-action"
          onClick={addColumn}
          data-testid="kpi-grid-column-add"
        >
          <FiPlus className="me-1" />
          Add column
        </Button>
      </div>

      {columns.length === 0 ? (
        <div className="text-muted small">No columns yet.</div>
      ) : (
        <div className="d-flex flex-column gap-3">
          {columns.map((col, index) => {
            const dtName =
              dataTypeById.get(Number(col.data_type_id))?.data_type_name ?? "";
            const showOptions =
              isSelectDataTypeName(dtName) || isMultiselectDataTypeName(dtName);
            const isNumeric = isNumericCompatibleDataTypeName(dtName);
            const isGridType = isGridDataTypeName(dtName);
            const { normal: colNormal, other: colOther } = splitColumnOptions(
              col.options ?? []
            );

            return (
              <div
                key={col.kpi_grid_column_id ?? `new-col-${index}`}
                className="border rounded p-3"
                data-testid={`kpi-grid-column-row-${index}`}
              >
                <div className="d-flex gap-2 align-items-start mb-2">
                  <Form.Control
                    type="text"
                    placeholder="Column label"
                    value={col.label}
                    onChange={(e) => updateColumn(index, { label: e.target.value })}
                  />
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    className="ntcog-table-action-btn--danger flex-shrink-0"
                    onClick={() => removeColumn(index)}
                    aria-label={`Remove column ${index + 1}`}
                  >
                    <FiTrash2 />
                  </Button>
                </div>

                <div className="row g-2">
                  <div className="col-md-6">
                    <Form.Label className="small mb-1">Data type</Form.Label>
                    <Form.Select
                      value={col.data_type_id || ""}
                      onChange={(e) =>
                        updateColumn(index, {
                          data_type_id: Number(e.target.value) || 0,
                        })
                      }
                    >
                      <option value="">Select type</option>
                      {dataTypes
                        .filter((dt) => !isGridDataTypeName(dt.data_type_name))
                        .map((dt) => (
                          <option key={dt.data_type_id} value={dt.data_type_id}>
                            {dt.label || dt.data_type_name}
                          </option>
                        ))}
                    </Form.Select>
                    {isGridType ? (
                      <Form.Text className="text-danger">
                        Nested GRID columns are not supported.
                      </Form.Text>
                    ) : null}
                  </div>
                  <div className="col-md-6 d-flex align-items-end">
                    <Form.Check
                      type="switch"
                      id={`grid-col-rollup-${index}`}
                      label="Include in rollup"
                      checked={!!col.include_in_rollup}
                      disabled={!isNumeric}
                      onChange={(e) =>
                        updateColumn(index, {
                          include_in_rollup: e.target.checked,
                        })
                      }
                    />
                  </div>
                </div>

                {showOptions ? (
                  <div className="mt-3">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <div className="small fw-semibold">Column options</div>
                      <div className="d-flex gap-2">
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={() =>
                            setColumnOptions(
                              index,
                              [
                                ...colNormal,
                                {
                                  label: "",
                                  list_order: colNormal.length,
                                  allows_input: false,
                                  is_active: true,
                                },
                              ],
                              colOther
                            )
                          }
                        >
                          <FiPlus className="me-1" />
                          Add option
                        </Button>
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          disabled={!!colOther}
                          onClick={() =>
                            setColumnOptions(index, colNormal, {
                              label: "Other",
                              list_order: colNormal.length,
                              allows_input: true,
                              is_active: true,
                            })
                          }
                        >
                          <FiPlus className="me-1" />
                          Add &quot;Other&quot;
                        </Button>
                      </div>
                    </div>
                    {colNormal.map((opt, optIndex) => (
                      <div
                        key={opt.kpi_grid_column_option_id ?? `opt-${optIndex}`}
                        className="d-flex gap-2 mb-2"
                      >
                        <Form.Control
                          type="text"
                          placeholder="Option label"
                          value={opt.label}
                          onChange={(e) => {
                            const nextNormal = colNormal.map((o, oi) =>
                              oi === optIndex
                                ? { ...o, label: e.target.value }
                                : o
                            );
                            setColumnOptions(index, nextNormal, colOther);
                          }}
                        />
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={() => {
                            setColumnOptions(
                              index,
                              colNormal.filter((_, oi) => oi !== optIndex),
                              colOther
                            );
                          }}
                        >
                          <FiTrash2 />
                        </Button>
                      </div>
                    ))}
                    {colOther ? (
                      <div className="d-flex gap-2 mb-2 align-items-center">
                        <Form.Control
                          type="text"
                          placeholder="Other"
                          value={colOther.label}
                          onChange={(e) =>
                            setColumnOptions(index, colNormal, {
                              ...colOther,
                              label: e.target.value,
                              allows_input: true,
                            })
                          }
                        />
                        <span className="badge text-bg-secondary flex-shrink-0">
                          Free text
                        </span>
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={() => setColumnOptions(index, colNormal, null)}
                        >
                          <FiTrash2 />
                        </Button>
                      </div>
                    ) : null}
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default KpiGridColumnsEditor;
